import Province from './Province'
import ObjectUtils from '../utils/ObjectUtils'
import Riding from './Riding'

// A province code will always have the region code as its first digit.
const provinceIsInRegion = (province: Province, region: Region): boolean => {
  const exists = region
    .provinces()
    .find((regionProvince: Province): boolean => {
      return regionProvince.key === province.key
    })
  return exists ? true : false
}

export default class Region {
  public static AL = new Region(1, 'Atlantic', 'Atlantic', [
    Province.NL,
    Province.NS,
    Province.PE,
    Province.NB
  ])
  public static QC = new Region(2, 'Quebec', 'Quebec', [Province.QC])
  public static ON = new Region(3, 'Ontario', 'Ontario', [Province.ON])
  public static PR = new Region(4, 'Prairies', 'Prairies', [
    Province.MB,
    Province.SK
  ])
  public static AB = new Region(45, 'Alberta', 'Alberta', [Province.AB])
  public static BC = new Region(5, 'British Columbia', 'BC', [Province.BC])
  public static TR = new Region(6, 'Territories', 'Terr.', [
    Province.YT,
    Province.NT,
    Province.NU
  ])

  private static REGION_ARR: Region[] = [
    Region.AL,
    Region.QC,
    Region.ON,
    Region.PR,
    Region.AB,
    Region.BC,
    Region.TR
  ]
  private static REGION_MAP = ObjectUtils.arrayToMap(
    Region.REGION_ARR,
    (r: Region): string => r.key
  )

  public static allRegionsArray(): Region[] {
    return Region.REGION_ARR
  }

  public static allRegionsMap(): { [key: string]: Region } {
    return Region.REGION_MAP
  }

  public static allRegionKeys(): string[] {
    return Object.keys(Region.REGION_MAP)
  }

  public static fromKey(regionKey: string): Region {
    return Region.REGION_MAP[regionKey]
  }

  public static fromProvince(province: Province): Region {
    const region = Region.allRegionsArray().find((region): boolean =>
      provinceIsInRegion(province, region)
    )
    if (!region) {
      throw new Error(`No region for province ${province.name}.`)
    }
    return region
  }

  protected _code: number // The Standard Geographical Classification (SGC) code
  protected _name: string // Name
  protected _shortName: string // Short name (Canada Post)
  protected _provincesDict: { [key: string]: Province } = {}

  public get code(): number {
    return this._code
  }

  public get key(): string {
    return this._shortName
  }

  public get name(): string {
    return this._name
  }

  public get shortName(): string {
    return this._shortName
  }

  public constructor(
    code: number,
    name: string,
    shortName: string,
    provinces: Province[]
  ) {
    this._code = code
    this._name = name
    this._shortName = shortName
    provinces.forEach((province: Province): void => {
      this._provincesDict[province.key] = province
    })
  }

  public ridings(): Riding[] {
    const ridings: Riding[] = []
    Object.values(this._provincesDict).forEach((province: Province): void => {
      province.ridings().forEach((riding: Riding): void => {
        ridings.push(riding)
      })
    })
    return ridings
  }

  public provinces(): Province[] {
    return Object.values(this._provincesDict)
  }
}
